import { useCallback, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface PortalProps {
  children: ReactNode
}

/** Render children into a dedicated <div> appended to <body> while mounted. */
export function Portal({ children }: PortalProps) {
  const [host] = useState(() => {
    const el = document.createElement('div')
    el.dataset.portal = ''
    return el
  })
  // Attach during render commit: append in a ref callback so the host exists
  // before children's effects run (focus traps need it in the document).
  // The callback must be stable: React 19 re-runs an inline ref's cleanup and
  // setup on every render, and re-inserting the host resets the scroll
  // position of everything inside it (a scrolled listbox jumped to the top).
  const attach = useCallback(
    (node: HTMLSpanElement | null) => {
      if (node && !host.isConnected) document.body.appendChild(host)
      return () => {
        host.remove()
      }
    },
    [host]
  )
  return (
    <>
      <span hidden ref={attach} />
      {createPortal(children, host)}
    </>
  )
}
